import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import UserBadge from './UserBadge';

const navItems = [
  {
    label: 'Research',
    link: '/research',
    children: [
      { name: 'All Publications', href: '/research' },
      { name: 'AI Automation Systems', href: '/research?category=AI+Automation+Systems' },
      { name: 'Agent Workflows', href: '/research?category=Agent+Workflows' },
      { name: 'Developer Productivity', href: '/research?category=Developer+Productivity' },
      { name: 'Local AI Infrastructure', href: '/research?category=Local+AI+Infrastructure' },
      { name: 'Data Pipelines', href: '/research?category=Data+Pipelines' },
    ],
  },
  {
    label: 'Products',
    link: '/products',
    children: [
      { name: 'Wallet King', href: 'https://wallet-king.app', external: true },
      { name: 'Eligibl', href: 'https://eligibl.org', external: true },
      { name: 'Caldera Compliance', href: 'https://calderacompliance.com', external: true },
    ],
  },
  {
    label: 'Consulting',
    link: '/consulting',
    children: [
      { name: 'AI & Automation', href: '/consulting/ai-automation' },
      { name: 'Software Engineering', href: '/consulting/software-engineering' },
      { name: 'Data & Analytics', href: '/consulting/data-analytics' },
      { name: 'CRM & Operations', href: '/consulting/crm-operations' },
      { name: 'Get a Free Consultation', href: '/consulting#contact' },
      { name: 'Security quote', href: '/security' },
    ],
  },
  {
    label: 'Engineering Log',
    link: '/blog',
    children: [
      { name: 'All Articles', href: '/blog' },
      { name: 'Engineering', href: '/blog?category=Engineering' },
      { name: 'AI & Automation', href: '/blog?category=AI+%26+Automation' },
      { name: 'Case Studies', href: '/blog?category=Case+Studies' },
      { name: 'Industry Insights', href: '/blog?category=Industry+Insights' },
      { name: 'Company Updates', href: '/blog?category=Company+Updates' },
    ],
  },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navigate = useNavigate();

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMobileExpanded(null);
    setActiveDropdown(null);
  }, []);

  const handleMobileNav = (href) => {
    closeMenu();
    if (/^https?:/.test(href)) {
      window.location.assign(href);
      return;
    }
    navigate(href);
  };

  return (
    <header className="site-header">
      {/* Top bar */}
      <div className="header-top">
        <div className="header-top-inner">
          <span className="header-notice">
            Vector Labs &middot; Charlotte, NC &middot; Applied AI Research &amp; Engineering
          </span>
          <div className="header-top-links">
            <Link to="/careers">Careers</Link>
            <Link to="/research">Research</Link>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="header-main">
        <div className="header-main-inner">
          <Link to="/" className="logo" onClick={closeMenu}>
            <span className="logo-text">
              <strong>Vector Labs</strong>
              <small>Applied AI Research</small>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="main-nav desktop-nav">
            <Link to="/" className="nav-engage">About</Link>
            <span className="nav-divider">|</span>
            {navItems.map((item, i) => (
              <div
                className="nav-item"
                key={item.label}
                onMouseEnter={() => setActiveDropdown(i)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link to={item.link || '#'} className="nav-btn">
                  {item.label}
                  {item.children && <FiChevronDown className="chevron" />}
                </Link>
                {item.children && activeDropdown === i && (
                  <div className="dropdown">
                    {item.children.map((child) => (
                      child.external ? (
                        <a key={child.name} href={child.href} target="_blank" rel="noreferrer">
                          {child.name}
                        </a>
                      ) : (
                        <Link key={child.name} to={child.href}>
                          {child.name}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="header-actions">
            <UserBadge />
            <div className="search-box">
              <input type="text" placeholder="Search" />
              <FiSearch />
            </div>
            <button
              className="menu-toggle"
              onClick={() => { setMenuOpen(!menuOpen); setMobileExpanded(null); }}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FiX /> : <FiMenu />}
              <span>Menu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav overlay */}
      {menuOpen && (
        <>
          <div className="mobile-nav-backdrop" onClick={closeMenu} />
          <nav className="mobile-nav">
            <button className="mobile-nav-link" onClick={() => handleMobileNav('/')}>About</button>
            {navItems.map((item, i) => (
              <div key={item.label} className="mobile-nav-group">
                <div className="mobile-nav-header">
                  <button
                    className="mobile-nav-link"
                    onClick={() => handleMobileNav(item.link)}
                  >
                    {item.label}
                  </button>
                  {item.children && (
                    <button
                      className="mobile-nav-expand"
                      onClick={() => setMobileExpanded(mobileExpanded === i ? null : i)}
                      aria-label={`Expand ${item.label}`}
                    >
                      <FiChevronRight className={mobileExpanded === i ? 'rotated' : ''} />
                    </button>
                  )}
                </div>
                {item.children && mobileExpanded === i && (
                  <div className="mobile-nav-children">
                    {item.children.map((child) => (
                      <button
                        key={child.name}
                        className="mobile-nav-child"
                        onClick={() => handleMobileNav(child.href)}
                      >
                        {child.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mobile-nav-footer">
              <UserBadge />
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
